import { classSet } from '@platform/ui-library';
import styles from './style/nested-list.module.scss';

type NestedValue = Record<string, any> | any[] | string | number | boolean | null | undefined;

type NestedListProps = {
  data: NestedValue;
  className?: string;
};

/**
 * A React component that generates nested <ul> and <li> elements
 * based on the structure of the input object, array, or primitive value.
 * The component uses CSS modules for styling.
 * @param {NestedValue} data - The input data to generate the list from.
 * @returns JSX.Element
 */
export const NestedList = ({ className, data }: NestedListProps) => {
  const renderContent = (data: NestedValue): JSX.Element => {
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <span className={styles.emptyArray}>empty</span>;
      }
      return (
        <ul>
          {data.map((item, index) => (
            <li key={index}>{renderContent(item)}</li>
          ))}
        </ul>
      );
    }
    if (typeof data === 'object' && data !== null) {
      return (
        <ul>
          {Object.entries(data).map(([key, value]) => {
            return (
              <li key={key}>
                <strong>{key}:</strong> {renderContent(value)}
              </li>
            );
          })}
        </ul>
      );
    }
    return renderPrimitive(data);
  };

  if (!data) {
    return null;
  }

  return <div className={classSet([styles.nestedListWrapper, className])}>{renderContent(data)}</div>;
};

/**
 * Parses and returns JSX for primitive values with appropriate classes.
 * @param {NestedValue} value - The primitive value to be parsed.
 * @returns JSX.Element
 */
const renderPrimitive = (value: NestedValue): JSX.Element => {
  let type = typeof value;
  if (value === 'null') {
    type = 'object';
  } else if (value === 'undefined') {
    type = 'undefined';
  }
  switch (type) {
    case 'undefined':
      return <span className={styles.undefined}>undefined</span>;
    case 'object':
      return <span className={styles.null}>null</span>;
    case 'boolean':
      return <span className={value ? styles.true : styles.false}>{value ? 'true' : 'false'}</span>;
    case 'string':
      return value ? (
        <span className={styles.string}>{value as string}</span>
      ) : (
        <span className={styles.emptyString}>empty</span>
      );
    case 'number':
      return <span className={styles.number}>{String(value)}</span>;
    default:
      return <span className={styles.unknown}>{String(value)}</span>;
  }
};
